import { useHasChanged } from 'Lib/Hooks';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../Data/ReduxViewModels/Lists';
import * as oPath from '../../Lib/Object/Path/Immutable';
import deepEquals from '../../Lib/Object/deepEquals';
import coalesce from '../../Lib/coalesce';
import { CreatableDropdown, Dropdown, IDropdownProps } from '../Core';

export interface IListDropdownProps<TValue = any> extends IDropdownProps {
  autoSelectedValue?: TValue;
  autoSelectSingleOption?: boolean;
  disabled?: boolean;
  endpoint?: string;
  initEndpoint?: string;
  id?: string;
  lists?: any;
  name?: string;
  onChange?: (_: any) => void;
  onListChange?: (event: { list: any; search: any }) => void;
  queryParams?: any;
  searchImmediately?: boolean;
  simpleValue?: boolean;
  value?: any;
  disabledOptions?: TValue[];
  /**
   * Defines additional options to include with those returned from the list endpoint.
   * Typically, this should be used for options with special behaviour such as an "all" option for a filter.
   * These options are currently always prepended to the list returned from the endpoint
   */
  additionalOptions?: Array<{ value: TValue; label: string }>;
}

let idCounter = 0;

/**
 * This is an extension to Dropdown and should be configured to retrieve its listed items from a specific
 * configured endpoint provided by the Web API. This Dropdown should always be wrapped and not used directly in views.
 ****/
let ListDropdown: React.FC<IListDropdownProps> = ({
  id,
  autoSelectSingleOption,
  onListChange,
  simpleValue,
  autoSelectedValue,
  name,
  onChange,
  value,
  searchImmediately,
  options,
  endpoint = '',
  initEndpoint = '',
  lists,
  queryParams,
  disabled,
  disabledOptions,
  additionalOptions,
  maxResults,
  ...otherProps
}) => {
  if (autoSelectSingleOption && !searchImmediately)
    throw new Error(
      'For ListDropdown components, when property autoSelectSingleOption is true then searchImmediately must also be true.'
    );
  if (autoSelectSingleOption && !onChange)
    throw new Error(
      'For ListDropdown components, when property autoSelectSingleOption is true then property onChange must also be set.'
    );
  const dispatch = useDispatch();
  const [dropdownId] = useState(coalesce(id, `list-dropdown-${idCounter++}`));
  const [lastSearch, setLastSearch] = useState(null);
  const [listChangeFired, setListChangeFired] = useState(false);
  const [selfDisabled, setSelfDisabled] = useState(false);
  const [prevQueryParams, setPrevQueryParams] = useState(queryParams);
  // The following variables come from the list dropdown's state. The reason that they have values when undefined
  // is so that there is no unneccessary re-rendering on first load.
  const firstSearchComplete = useSelector((s) => oPath.get(s.lists, dropdownId)?.firstSearchComplete ?? false);
  const searching = useSelector((s) => oPath.get(s.lists, dropdownId)?.searching ?? false);
  const listItems = useSelector((s) => {
    const items = [...(additionalOptions ?? []), ...(oPath.get(s.lists, dropdownId)?.items ?? [])];
    return items.map((d: any) => ((disabledOptions?.indexOf(d.value) ?? -1) >= 0 ? { ...d, isDisabled: true } : d));
  }, deepEquals);
  const freeEntry = useSelector((s) => oPath.get(s.lists, dropdownId)?.options?.freeEntry ?? false);
  const reinitialise = useHasChanged(dropdownId);
  const [prevSearchComplete, setPrevSearchComplete] = useState(firstSearchComplete);
  const [prevListItems, setPrevListItems] = useState(listItems);

  const handleInputChange = React.useCallback(
    (search: any) => {
      if (lastSearch !== search) {
        setLastSearch(search);
        dispatch(actions.get({ id: dropdownId, params: { ...queryParams, search, limit: maxResults }, endpoint }));
      }
    },
    [dispatch, lastSearch, queryParams, dropdownId, endpoint, maxResults]
  );

  useEffect(() => {
    if (reinitialise) {
      dispatch(actions.init({ id: dropdownId, params: { ...queryParams, limit: maxResults }, endpoint: initEndpoint }));
      if (searchImmediately) {
        handleInputChange('');
      }
    }
  }, [dispatch, reinitialise, handleInputChange, dropdownId, queryParams, initEndpoint, searchImmediately, maxResults]);

  useEffect(() => {
    return () => {
      dispatch(actions.cleanup({ id: dropdownId }));
    };
  }, [dispatch, dropdownId]);

  useEffect(() => {
    if (firstSearchComplete && !deepEquals(queryParams, prevQueryParams)) {
      dispatch(
        actions.get({ id: dropdownId, params: { ...queryParams, search: lastSearch, limit: maxResults }, endpoint })
      );
    }
  }, [dispatch, dropdownId, firstSearchComplete, queryParams, prevQueryParams, lastSearch, endpoint, maxResults]);

  useEffect(() => {
    // autoSelectSingleOption
    if (
      autoSelectSingleOption &&
      onChange && // Must have an onChange event so we can trigger the change.
      !prevSearchComplete && // Only auto select on first (empty) search. We don't want to autoselect from a filtered list.
      firstSearchComplete &&
      listItems?.length === 1
    ) {
      onChange?.({
        data: { name, value: simpleValue ? listItems[0]?.value : listItems[0], submitValues: true },
      });
      setSelfDisabled(true);
    }
  }, [autoSelectSingleOption, listItems, firstSearchComplete, prevSearchComplete, simpleValue, onChange, name]);

  useEffect(() => {
    // autoSelectedValue
    // Distinct from default value as it gets selected only once the list is loaded
    // and only if the value is valid
    if (
      autoSelectedValue &&
      onChange && // Must have an onChange event so we can trigger the change.
      !prevSearchComplete && // Set default on first (empty) search.
      firstSearchComplete &&
      !value
    ) {
      const autoSelectedListItem = listItems.find((i: any) => i && i.value === autoSelectedValue);
      onChange({
        data: {
          name,
          value: simpleValue ? autoSelectedListItem?.value : autoSelectedListItem,
          submitValues: true,
        },
      });
    }
  }, [autoSelectedValue, firstSearchComplete, listItems, prevSearchComplete, simpleValue, onChange, name, value]);

  useEffect(() => {
    // onListChange event
    if (onListChange && firstSearchComplete && (!listChangeFired || !deepEquals(listItems, prevListItems))) {
      setListChangeFired(true);
      onListChange?.({ list: listItems, search: lastSearch });
    }
  }, [dispatch, listChangeFired, firstSearchComplete, prevListItems, lastSearch, onListChange, listItems]);

  useEffect(() => {
    if (!deepEquals(prevListItems, listItems)) setPrevListItems(listItems);
  }, [listItems, prevListItems]);

  useEffect(() => {
    if (firstSearchComplete !== prevSearchComplete) setPrevSearchComplete(firstSearchComplete);
  }, [firstSearchComplete, prevSearchComplete]);

  useEffect(() => {
    if (queryParams !== prevQueryParams) setPrevQueryParams(queryParams);
  }, [queryParams, prevQueryParams]);

  const menuOpen = () => {
    // Don't load list until first time dropdown is clicked, then load it when menu is first opened.
    if (lastSearch === null) handleInputChange('');
  };

  return freeEntry ? (
    <CreatableDropdown
      id={dropdownId}
      onInputChange={handleInputChange}
      onMenuOpen={menuOpen}
      options={listItems}
      isLoading={searching}
      disabled={disabled || selfDisabled}
      formatCreateLabel={(label: string) => `Other: ${label}`}
      {...{
        simpleValue,
        name,
        onChange,
        value,
        maxResults,
        ...otherProps,
      }}
    />
  ) : (
    <Dropdown
      id={dropdownId}
      onInputChange={handleInputChange}
      onMenuOpen={menuOpen}
      options={listItems}
      isLoading={searching}
      disabled={disabled || selfDisabled}
      {...{
        simpleValue,
        name,
        onChange,
        value,
        maxResults,
        ...otherProps,
      }}
    />
  );
};

export { ListDropdown };
export default ListDropdown;
